// @ts-nocheck
/** *****
 * Store Custom Code:
 * For updates, compare with: modules\content-library\src\Widgets\Common\HeaderSignIn.tsx
 * ****** */
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import User from "@insite/mobius/Icons/User";
import Menu, { MappedLink } from "@insite/mobius/Menu";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

/* Store Custom Code - Start */
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { IconWrapper } from "@insite/mobius/Icon/Icon";
import Popover, { PopoverPresentationProps } from "@insite/mobius/Popover";
import getColor from "@insite/mobius/utilities/getColor";
import ChevronDown from "@store/Library/Components/Toolkit/Icons/ChevronDown";
import PersonFill from "@store/Library/Components/Toolkit/Icons/Person/PersonFill";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import constants from "@store/constants";
import SideMenu, { SideMenuStyles } from "@store/Library/Components/Toolkit/SideMenu";
import { linkStyles } from "@store/Themes/common/mixins/link";
import getCssProp from "@toolkit/Framework/Common/Utilities/getCssProp";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Store Custom Code - End */

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
}

const mapStateToProps = (state: ApplicationState) => ({
    isSignInPage: getCurrentPage(state).type === "SignInPage",
    userName: state.context.session.userLabel, // Store Custom Code
    myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    isPunchOutSession: getIsPunchOutSession(state),
    currentLocation: getLocation(state),
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
    showInventory: state.context.showInventory,
    showPricing: state.context.showPricing,
    isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
    hasChangeLastQty:
        state.pages.vmiBins.selectVmiBinsChangedQty &&
        Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
    // Store Custom Code
    isSignedIn: state.context.session.userName && !getCurrentUserIsGuest(state),
    customerName: getShipToState(state, state.context.session.shipToId).value?.customerName,
    customerSequence: getShipToState(state, state.context.session.shipToId).value?.customerSequence,
});

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    // Store Custom Code
    userNameTypography?: InjectableCss;
    userNameWrapper?: InjectableCss;
    titleTypographyWrapper?: InjectableCss;
    titleTypographyIcon?: IconPresentationProps;
    popover?: PopoverPresentationProps;
    popoverContent?: InjectableCss;
    sideMenu?: SideMenuStyles;
}

export const headerSignInStyles: HeaderSignInStyles = {
    /* Store Custom Code - Start */
    signOutWrapper: {
        css: css`
            display: flex;
            padding: ${pxToRem(5)} 0;
        `,
    },
    titleClickable: {
        css: css`
            &:hover,
            &[aria-expanded="true"] {
                ${IconWrapper} {
                    color: ${getColor("primary")};
                }
            }
        `,
    },
    titleIcon: {
        size: 30,
        css: css`
            color: ${getColor("icon")};
            transition: ${transitionHoverStyles()};
        `,
    },
    userNameWrapper: {
        css: css`
            margin-left: ${pxToRem(10)};
        `,
    },
    userNameTypography: {
        css: css`
            display: block;
            color: ${getColor("primary")};
            font-weight: ${x => x.theme.design.typography.weight.base};
            line-height: ${x => x.theme.design.typography.lineHeight.smaller};
            text-transform: none;
        `,
    },
    titleTypographyWrapper: {
        css: css`
            display: flex;
        `,
    },
    titleTypography: {
        css: css`
            margin-right: ${pxToRem(10)};
        `,
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: ${pxToRem(30)};
        `,
    },
    titleTypographyIcon: {
        src: ChevronDown,
        css: css`
            transform: translateY(-${pxToRem(3)});
        `,
    },
    popover: {
        contentBodyProps: {
            css: css`
                max-height: none;
                color: ${getColor("text")};
                min-width: ${pxToRem(220)};
                padding: ${pxToRem(25)};
                ${getCssProp("box-shadow", "theme.shadows.light60")}
            `,
        },
    },
    sideMenu: {
        wrapper: null as any,
        listItem: null as any,
        link: {
            css: css`
                &&& {
                    ${linkStyles("primary", null, false, true)}
                    text-transform: none;
                }
                padding: ${pxToRem(6, 10)};
                width: 100%;
            `,
        },
    },
    /* Store Custom Code - End */
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;

export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    // Store Custom Code
    isSignedIn,
    customerName,
    customerSequence,
}: Props) => {
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);

    /* Store Custom Code - Start */
    const element = React.useRef<HTMLUListElement>(null);
    const popover = React.useRef<HTMLUListElement>(null);
    /* Store Custom Code - End */

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    if (!myAccountPageLink) {
        return null;
    }

    const { visibilityState, includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    if (fields.icon === "User") {
        icon = PersonFill; // Store Custom Code
    }

    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = (myAccountPageLink?.children?.filter(
        pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    ) ?? []) as MappedLink[];

    const getMenuItem = (title: "Inventory" | "Pricing"): MappedLink => {
        return {
            title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
            iconProps: {
                src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
                size: 22,
                css: css`
                    margin-left: 20px;
                    margin-right: -12px;
                `,
            },
            clickableProps: {
                css: css`
                    display: flex;
                    text-transform: uppercase;
                `,
                onClick: () => {
                    title === "Inventory"
                        ? setShowInventory({ showInventory: !showInventory })
                        : setShowPricing({ showPricing: !showPricing });
                },
            },
        };
    };

    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");

        showInventoryToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Inventory"));
        showPricingToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Pricing"));
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            e.preventDefault();

            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
                return;
            }

            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
            } else {
                const returnUrl = currentLocation.pathname + currentLocation.search;
                if (returnUrl === "/") {
                    history.push(`${signInUrl}`);
                } else {
                    history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
                }
            }
        }
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let signInStatusText = translate("Sign In");
    if (userName && !currentUserIsGuest) {
        signInStatusText = userName;
    }

    /* Store Custom Code - Start */
    const closePopover = () => {
        popover.current && (popover.current! as any).closePopover();
    };

    const loginButton = (
        <Clickable {...styles.titleClickable} data-test-selector="header_signIn">
            {showIcon && <Icon {...styles.titleIcon} src={icon} />}
            {showLabel && (
                <StyledWrapper {...styles.userNameWrapper}>
                    <Typography {...styles.userNameTypography} data-test-selector="header_userName">
                        {userName}
                    </Typography>
                    <StyledWrapper {...styles.titleTypographyWrapper}>
                        <Typography {...styles.titleTypography}>{translate("My Account")}</Typography>
                        <Icon {...styles.titleTypographyIcon} />
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </Clickable>
    );
    /* Store Custom Code - End */

    return (
        <StyledWrapper {...styles.signOutWrapper}>
            {/* Store Custom Code - Start */}
            {isSignedIn ? (
                <Popover
                    {...styles.popover}
                    contentBodyProps={{
                        ...styles.popover?.contentBodyProps,
                        as: "div",
                        ref: element,
                    }}
                    positionFunction={() => ({
                        position: "absolute",
                        right: 0,
                    })}
                    ref={popover}
                    insideRefs={[element]}
                    popoverTrigger={loginButton}
                >
                    <StyledWrapper {...styles.popoverContent}>
                        <SideMenu
                            pageType={constants.pageType.myAccount}
                            showRootPage={fields.includeAccountMenu}
                            showSignOut
                            onClick={closePopover}
                            overridedStyles={styles.sideMenu}
                            showRestrictedToggle
                        />
                    </StyledWrapper>
                </Popover>
            ) : (
                <Clickable {...styles.titleClickable} onClick={onSignInHandler} data-test-selector="header_signIn">
                    {showIcon && <Icon {...styles.titleIcon} src={icon} />}
                    {showLabel && <Typography {...styles.titleTypography}>{translate("Sign In")}</Typography>}
                </Clickable>
            )}
            {/* Store Custom Code - End */}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
